/**
 * Mobile layout for Home: banner, carousel, "С чего начать?", "Жизнь церкви", "Есть вопросы?".
 */

import { Link } from "react-router-dom";
import { ListSkeleton } from "../../components/ui/Skeleton.tsx";
import {
  IconMessage,
  IconEnvelope,
  IconMapPin,
  IconReceipt,
} from "../../components/Icons.tsx";
import { EventCarousel } from "./EventCarousel.tsx";
import { QuickActionsGrid } from "./QuickActionsGrid.tsx";
import { StartCards } from "./HomePageShared.tsx";
import { BOTTOM_LINKS } from "../../config/home.ts";
import type { Event } from "../../lib/graphql.ts";

interface HomePageMobileProps {
  events: Event[];
  isLoading: boolean;
}

export function HomePageMobile({ events, isLoading }: HomePageMobileProps) {
  return (
    <>
      {/* 1. Анонсы — без карточки */}
      <section className="mt-0 w-full min-w-0">
        {isLoading ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-6">
            <ListSkeleton count={2} />
          </div>
        ) : (
          <EventCarousel events={events} />
        )}
      </section>

      {/* 2. С чего начать? — карточка */}
      <section className="mt-5">
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-4 flex justify-center">
          <div className="grid grid-cols-3 gap-4 max-w-[360px] sm:max-w-[400px]">
            <StartCards />
          </div>
        </div>
      </section>

      {/* 3. Жизнь церкви — карточка, лента выходит за край (частичный 4-й блок) */}
      <section className="mt-6 -mx-3 xs:-mx-4 min-[430px]:-mx-5 min-[520px]:-mx-6 sm:-mx-7 lg:mx-0">
        <div className="rounded-2xl overflow-x-visible overflow-y-hidden lg:overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-4">
          <p className="section-title px-0">Жизнь церкви</p>
          <div className="mt-1">
            <QuickActionsGrid />
          </div>
        </div>
      </section>

      {/* 4. Есть вопросы? — карточки */}
      <section className="mt-6">
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-4">
          <p className="section-title px-0 mb-0">Есть вопросы? Мы рядом</p>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {BOTTOM_LINKS.map((item) => {
              const Icon =
                item.icon === "message"
                  ? IconMessage
                  : item.icon === "envelope"
                    ? IconEnvelope
                    : item.icon === "mapPin"
                      ? IconMapPin
                      : IconReceipt;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl bg-slate-50/80 border border-slate-100 active:bg-slate-100 transition-colors no-underline text-inherit min-h-[72px]"
                  aria-label={item.label}
                >
                  <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center text-slate-600">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-medium text-slate-700 text-center leading-tight">
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom spacing for tab bar */}
      <div className="h-4" />
    </>
  );
}
